/**
 * Cifrado zero-knowledge para "compartir por enlace" (`/api/share`).
 *
 * Mismo formato que `scripts/share-smoke.js`: AES-256-GCM, iv de 12 bytes,
 * todo en base64url. La clave K se genera en el cliente y nunca viaja al
 * servidor — solo el fragmento de la URL (`/s/<id>#<K>`) la lleva, y el
 * navegador nunca envía el fragmento en la petición HTTP.
 *
 * `passwordHash` (opcional) = SHA-256(password + id) hex, ver README
 * (sección "Diseño zero-knowledge") y server/routes/share.js.
 */

function toBase64Url(buf) {
  const bytes = new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(str) {
  const padded = str.replace(/-/g, '+').replace(/_/g, '/').padEnd(str.length + (4 - (str.length % 4 || 4)) % 4, '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Genera K, cifra `plainObj` y devuelve lo que se manda al servidor
 * (`ciphertext`, `iv`) más la clave que va en el fragmento de la URL.
 */
export async function encryptForShare(plainObj) {
  const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(plainObj));
  const ciphertextBuf = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  const rawKey = await crypto.subtle.exportKey('raw', key);

  return {
    ciphertext: toBase64Url(ciphertextBuf),
    iv: toBase64Url(iv),
    keyBase64url: toBase64Url(rawKey)
  };
}

/**
 * Descifra con la clave K (del fragmento de la URL) lo que devuelve
 * `POST /api/share/public/:id/open`.
 */
export async function decryptFromShare({ ciphertext, iv, keyBase64url }) {
  const key = await crypto.subtle.importKey('raw', fromBase64Url(keyBase64url), { name: 'AES-GCM' }, false, ['decrypt']);
  const plaintextBuf = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64Url(iv) }, key, fromBase64Url(ciphertext));
  return JSON.parse(new TextDecoder().decode(plaintextBuf));
}

/**
 * Hash de la contraseña opcional del enlace. El README describe la fórmula
 * como SHA-256(password + id), pero el `id` es el de la propia fila
 * `shared_links` y lo genera el servidor DESPUÉS de crear el enlace
 * (`crypto.randomBytes` en server/routes/share.js) — no hay forma de que el
 * creador lo conozca a tiempo para incluirlo en el `POST /api/share` inicial,
 * y no existe un endpoint para fijar `password_hash` después de crear la fila.
 * `server/routes/share.js` solo compara el hash guardado byte a byte contra
 * lo que mande `POST /public/:id/open`; no exige ningún formato concreto.
 * Así que aquí se hashea solo la contraseña: autoconsistente en creación y
 * apertura, sin depender de un dato que el creador no puede tener todavía.
 * [SIN VERIFICAR con Ferran: si se añade un endpoint para fijar el hash tras
 * crear el enlace, esto debería pasar a SHA-256(password + id) tal cual dice
 * el README.]
 */
export async function hashSharePassword(password) {
  const data = new TextEncoder().encode(password);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
